import authService from "./authService";
import httpService from "./httpService";
import jwtDecode from "jwt-decode";

const KEY = "wirepackage.oauth.signup";
const failure = () => new Error("Google signup session expired or unavailable. Please sign in again.");

function claims(token) {
    try {
        const value = jwtDecode(token.replace(/^Bearer /, ""));
        if (!value || typeof value.sub !== "string" || !value.sub ||
            !Number.isFinite(value.exp) || value.exp * 1000 <= Date.now()) throw failure();
        return value;
    } catch (_) {
        throw failure();
    }
}

async function fingerprint(token) {
    const bytes = new TextEncoder().encode(token);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}

export function clearGoogleSignup() {
    // This marker is UI recovery data, never an authentication credential.
    try {
        sessionStorage.removeItem(KEY);
    } catch (_) { /* Storage may be denied. */
    }
}

export async function beginGoogleSignup(token, referralCode) {
    const bearer = "Bearer " + token;
    claims(bearer);
    const marker = {version: 1, fingerprint: await fingerprint(bearer), referralCode};
    // Store no second JWT. authService owns the sole persistent token and HTTP header.
    sessionStorage.setItem(KEY, JSON.stringify(marker));
    try {
        authService.updateToken(bearer);
    } catch (_) {
        clearGoogleSignup();
        throw failure();
    }
}

export async function restoreGoogleSignup() {
    let token, marker;
    try {
        token = authService.getJwt();
        claims(token);
        marker = JSON.parse(sessionStorage.getItem(KEY));
        if (!marker || marker.version !== 1 || marker.fingerprint !== await fingerprint(token)) throw failure();
    } catch (_) {
        clearGoogleSignup();
        throw failure();
    }
    // Decoding is only an expiry precheck. The existing bearer filter verifies the
    // signature, expiry and account against the backend before any form is restored.
    await httpService.get(process.env.REACT_APP_API_ENDPOINT + "/account/organizations", {
        headers: {Authorization: token},
    });
    if (authService.getJwt() !== token || sessionStorage.getItem(KEY) !== JSON.stringify(marker)) throw failure();
    claims(token);
    httpService.setJwt(token);
    return marker.referralCode || 0;
}
